import useSWR, { useSWRConfig } from 'swr';
import userCache, { useUser } from '@mentimeter/user';
import {
  getCountRequest,
  getCountResponse,
  type GetCountResponse,
} from '@core-api/workspace-members/workspaces/{workspace_id}/members/count';
import { useCallback } from 'react';

export type WorkspaceMemberCount = GetCountResponse;

function useCacheKey(): string | null {
  const workspaceId = useUser().user?.workspaceId;
  if (!workspaceId) return null;
  return `${useCurrentWorkspaceMemberCount.name}-${workspaceId}`;
}

export function useInvalidateCurrentWorkspaceMemberCount() {
  const key = useCacheKey();
  const config = useSWRConfig();
  return useCallback(() => key && config.mutate(key), [key, config]);
}

export function useCurrentWorkspaceMemberCount():
  | WorkspaceMemberCount
  | undefined {
  const workspaceId = useUser().user?.workspaceId;
  const key = useCacheKey();
  const countQuery = useSWR(
    key,
    async () => {
      if (!workspaceId) return;
      const response = await fetch(
        getCountRequest({
          region: userCache.region,
          userAuth: userCache.getToken(),
          workspaceId,
        }),
      );
      return getCountResponse(response, { enableStatusCheck: true });
    },
    { revalidateOnFocus: false, revalidateIfStale: false },
  );
  if (!countQuery.data) return;
  return {
    active: countQuery.data.active,
    pending: countQuery.data.pending,
    all: countQuery.data.all,
    memberLite: countQuery.data.memberLite,
  };
}
